// Constants
import { WatchlistProviders } from 'app/modules/detectionModels/components/scenarioWidgets/watchlist/enums';
import { DEFAULT_DATA_MIGRATION_LAG } from 'app/modules/rules/constants';
import { UNLOADED_FLAGS_KEY } from 'app/shared/featureFlags/constants';
import { FeatureFlag } from 'app/shared/featureFlags/models';
import { createSelector } from 'reselect';

export const selectFeatureFlags = (state: RootState) => {
  return state.featureFlags.flags;
};

// Loading
export const selectFeatureFlagsLoading = (state: RootState) => {
  return state.featureFlags.flags[UNLOADED_FLAGS_KEY] === true;
};

export const selectRemoveDisplayTagsLogic = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.REMOVE_DISPLAY_TAGS],
);

export const selectSixtranSnowflakeExpectedLag = createSelector(
  selectFeatureFlags,
  (featureFlags) =>
    featureFlags[FeatureFlag.SIXTRAN_SNOWFLAKE_EXPECTED_LAG] ||
    DEFAULT_DATA_MIGRATION_LAG,
);

export const selectBulkActionLimit = createSelector(
  selectFeatureFlags,
  // default to 500 in case launchdarkly goes down
  (featureFlags): number => featureFlags[FeatureFlag.BULK_ACTION_LIMIT] || 500,
);

export const selectAlertFromAlertEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.ALERT_FROM_ALERT],
);

export const selectImmutableNotesEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.IMMUTABLE_NOTES],
);

// SC #116590 — entity/instrument grouping for schedules + "Get More".
export const selectAlertGroupingModesEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) =>
    featureFlags[FeatureFlag.ROUND_ROBIN_GROUP_BY_ENTITY_INSTRUMENT],
);

export const selectSavedFiltersEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.SAVED_FILTERS],
);

export const selectSessionReplayEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.SESSION_REPLAY],
);

export const selectClassifiedQueuesEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.CLASSIFIED_QUEUES],
);

export const selectAchRiskScoreScenarioEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.ACH_RISK_SCORE_SCENARIO_ENABLED],
);

export const selectFocsecFieldsEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.FOCSEC_FIELDS_ENABLED],
);

export const selectTrmDmbFlagEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.SR_TRM_DMB_ENABLED],
);

/** NON-STANDARD FeatureFlag, this is a temporary fix
 * to appease seeing the $ when currency is formatted
 */
export const selectWorkaroundHideCurrencyCode = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.WORKAROUND_HIDE_CURRENCY_CONFIG],
);

export const selectFraudTransactionRulesEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.FRAUD_TRANSACTION_RULES],
);

export const selectAchRiskScoreFieldEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.ACH_RISK_SCORE_FIELD],
);

export const selectRuleDelayFlag = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.RULE_DELAY],
);

export const selectDispositionActions = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.DISPOSITION_ACTIONS],
);

export const selectRuleEventSchemaV21Enabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.RULE_EVENT_SCHEMA_V2_1],
);

export const selectWatchlistAlertsEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.WATCHLIST_ALERTS],
);

export const selectAlertTableAmountColumnSkeletonLoading = createSelector(
  selectFeatureFlags,
  (featureFlags) =>
    featureFlags[FeatureFlag.ALERT_TABLE_AMOUNT_COLUMN_SKELETON_LOADING],
);

export const selectQualityCycleLimit = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.QUALITY_CYCLE_LIMIT],
);

export const selectDarkWebAlertsEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.DARK_WEB_ALERTS],
);

export const selectRuleLifecycleBannerEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.RULE_LIFECYCLE_BANNER],
);

export const selectWatchlistProvider = createSelector(
  selectFeatureFlags,
  (featureFlags) =>
    featureFlags[FeatureFlag.WATCHLIST_PROVIDER] || WatchlistProviders.UNIT22,
);

export const selectFeedbackSurveyID = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.FEEDBACK_SURVEY_ID] ?? 0,
);

export const selectDLEnabledForAlertList = createSelector(
  selectFeatureFlags,
  (featureFlags) => Boolean(featureFlags[FeatureFlag.CM_DELTA_LAKE_ALERT_LIST]),
);

export const selectDLEnabledForAlertDetails = createSelector(
  selectFeatureFlags,
  (featureFlags) =>
    Boolean(featureFlags[FeatureFlag.CM_DELTA_LAKE_ALERT_DETAIL]),
);

export const selectDLEnabledForAlertTxnAnalysis = createSelector(
  selectFeatureFlags,
  (featureFlags) =>
    Boolean(featureFlags[FeatureFlag.CM_DELTA_LAKE_ALERT_TXN_ANALYSIS]),
);

export const selectDLEnabledForCaseTxnAnalysis = createSelector(
  selectFeatureFlags,
  (featureFlags) =>
    Boolean(featureFlags[FeatureFlag.CM_DELTA_LAKE_CASE_TXN_ANALYSIS]),
);

export const selectDLEnabledForTxnEventDetails = createSelector(
  selectFeatureFlags,
  (featureFlags) =>
    Boolean(featureFlags[FeatureFlag.CM_DELTA_LAKE_TXN_EVENT_DETAIL]),
);

export const selectDLEnabledForAlertTxnList = createSelector(
  selectFeatureFlags,
  (featureFlags) =>
    Boolean(featureFlags[FeatureFlag.CM_DELTA_LAKE_ALERT_TXN_LIST]),
);

export const selectDLEnabledForAlertActionEvents = createSelector(
  selectFeatureFlags,
  (featureFlags) =>
    Boolean(featureFlags[FeatureFlag.CM_DELTA_LAKE_ALERT_ACTION_LIST]),
);

export const selectDLEnabledForAlertHistogram = createSelector(
  selectFeatureFlags,
  (featureFlags) =>
    Boolean(featureFlags[FeatureFlag.CM_DELTA_LAKE_ALERT_HISTOGRAM]),
);

export const selectAlertHitCgdoHistoricalLookupEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) =>
    Boolean(
      featureFlags[FeatureFlag.DM_ALERT_HIT_CGDO_HISTORICAL_LOOKUP_ENABLED],
    ),
);

export const selectAlertHitCgdoHistoricalLookupForAllAgents = createSelector(
  selectFeatureFlags,
  (featureFlags) =>
    Boolean(
      featureFlags[
        FeatureFlag.DM_ALERT_HIT_CGDO_HISTORICAL_LOOKUP_FOR_ALL_AGENTS
      ],
    ),
);

export const selectDLEnabledForCaseList = createSelector(
  selectFeatureFlags,
  (featureFlags) => Boolean(featureFlags[FeatureFlag.CM_DELTA_LAKE_CASE_LIST]),
);

export const selectDLEnabledForCaseDetail = createSelector(
  selectFeatureFlags,
  (featureFlags) =>
    Boolean(featureFlags[FeatureFlag.CM_DELTA_LAKE_CASE_DETAIL]),
);

export const selectDLEnabledForSARDetail = createSelector(
  selectFeatureFlags,
  (featureFlags) =>
    Boolean(featureFlags[FeatureFlag.CM_DELTA_LAKE_FILING_SAR_DETAIL]),
);

export const selectDLEnabledForSARList = createSelector(
  selectFeatureFlags,
  (featureFlags) =>
    Boolean(featureFlags[FeatureFlag.CM_DELTA_LAKE_FILING_SAR_LIST]),
);

export const selectDLEnabledForSARCreate = createSelector(
  selectFeatureFlags,
  (featureFlags) =>
    Boolean(featureFlags[FeatureFlag.CM_DELTA_LAKE_FILING_SAR_CREATE]),
);

export const selectDLEnabledForGoAMLCreate = createSelector(
  selectFeatureFlags,
  (featureFlags) =>
    Boolean(featureFlags[FeatureFlag.CM_DELTA_LAKE_FILING_GOAML_CREATE]),
);

export const selectDLEnabledForCTRCreate = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.CM_DELTA_LAKE_FILING_CTR_CREATE],
);

export const selectDLEnabledForFintracStrCreate = createSelector(
  selectFeatureFlags,
  (featureFlags) =>
    featureFlags[FeatureFlag.CM_DELTA_LAKE_FILING_FINTRAC_CREATE],
);

export const selectDLEnabledForTxnEventList = createSelector(
  selectFeatureFlags,
  (featureFlags) =>
    Boolean(featureFlags[FeatureFlag.CM_DELTA_LAKE_TXN_EVENT_LIST]),
);

export const selectDLEnabledForActionEventDetail = createSelector(
  selectFeatureFlags,
  (featureFlags) =>
    Boolean(featureFlags[FeatureFlag.CM_DELTA_LAKE_ACTION_EVENT_DETAIL]),
);

export const selectDLEnabledForActionEventList = createSelector(
  selectFeatureFlags,
  (featureFlags) =>
    Boolean(featureFlags[FeatureFlag.CM_DELTA_LAKE_ACTION_EVENT_LIST]),
);

export const selectDLEnabledForInstrumentList = createSelector(
  selectFeatureFlags,
  (featureFlags) =>
    Boolean(featureFlags[FeatureFlag.CM_DELTA_LAKE_INSTRUMENT_LIST]),
);

export const selectDLEnabledForInstrumentDetail = createSelector(
  selectFeatureFlags,
  (featureFlags) =>
    Boolean(featureFlags[FeatureFlag.CM_DELTA_LAKE_INSTRUMENT_DETAIL]),
);

export const selectDLEnabledForEntityList = createSelector(
  selectFeatureFlags,
  (featureFlags) =>
    Boolean(featureFlags[FeatureFlag.CM_DELTA_LAKE_ENTITY_LIST]),
);

export const selectDLEnabledForEntityDetails = createSelector(
  selectFeatureFlags,
  (featureFlags) =>
    Boolean(featureFlags[FeatureFlag.CM_DELTA_LAKE_ENTITY_DETAIL]),
);

export const selectDLEnabledForNetworkAnalysis = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.CM_DELTA_LAKE_NETWORK_ANALYSIS],
);

export const selectDLEnabledForEntityRiskScore = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.CM_DELTA_LAKE_ENTITY_RISK_SCORE],
);

// When enabled, entity-detail endpoints that previously took external_id in
// the URL path are routed through their /entities/cached/<hashed_id>/...
// equivalents so external_ids (PII) never appear in access logs.
export const selectHashedIdRoutesEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.CM_DELTA_LAKE_HASHED_ID_ROUTES],
);

export const selectHistoricRiskScoresEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.HISTORIC_RISK_SCORES],
);

export const selectBulkResolveFromQueuedViewsEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.BULK_RESOLVE_FROM_QUEUED_VIEWS],
);

export const selectFinCEN314aEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.FINCEN_314A],
);

export const selectFinCEN314aMatchingLogicVersion = createSelector(
  selectFeatureFlags,
  (featureFlags) =>
    featureFlags[FeatureFlag.FINCEN_314A_MATCHING_LOGIC_VERSION],
);

export const selectAiAgentEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.AI_AGENT],
);

export const selectAiAgentDataExplorationEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.AI_AGENT_DATA_EXPLORATION],
);

export const selectAskYourAlertEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.ASK_YOUR_ALERT],
);

export const selectFilingAiAgentsEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.ENABLE_FILING_AI_AGENTS],
);

export const selectAiDecisioningV0Enabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.AI_DECISIONING_V0],
);

export const selectSearchV2Enabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.SEARCH_V2],
);

export const selectEntityPhoneNumberSearchV2Enabled = createSelector(
  selectFeatureFlags,
  (featureFlags) =>
    featureFlags[FeatureFlag.ENTITY_PHONE_NUMBER_SEARCH_V2] &&
    featureFlags[FeatureFlag.SEARCH_V2],
);

export const selectSearchV2UseDlHashIdEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) =>
    featureFlags[FeatureFlag.CM_DELTA_LAKE_SEARCH_V2_USE_DL_HASH_ID] ?? false,
);

export const selectAllowAiAgentReRun = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.ALLOW_AI_AGENT_RE_RUN],
);

export const selectDmbCustomTxnDatetimeEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.DMB_CUSTOM_TXN_DATETIME],
);

export const selectG2ReviewEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.G2_REVIEW],
);

export const selectFintracStrAutofillWorkflowEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.FINTRAC_STR_AUTOFILL_WORKFLOW],
);

export const selectFintracStrAutofillDispositionCommentEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) =>
    featureFlags[FeatureFlag.FINTRAC_STR_AUTOFILL_DISPOSITION_COMMENT],
);

export const selectFintracStrEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.FINTRAC_STR],
);

export const selectFintracStrUploadEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.FINTRAC_STR_UPLOAD],
);

export const selectFintracFormGeneratorRefactorEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.FINTRAC_FORM_GENERATOR_REFACTOR],
);

export const selectFincenDoepFilingEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.FINCEN_DOEP_FILING],
);

export const selectEquifaxDemoEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.EQUIFAX_DEMO],
);

export const selectFraudFighterLogoEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.FRAUD_FIGHTER_LOGO],
);

export const selectSarVirtualizationEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.SAR_VIRTUALIZATION],
);

export const selectVelocityRtrEntityInstrumentAggregationFiltersEnabled =
  createSelector(
    selectFeatureFlags,
    (featureFlags) =>
      featureFlags[
        FeatureFlag.VELOCITY_RTR_ENTITY_INSTRUMENT_AGGREGATION_FILTERS
      ],
  );

export const selectVelocityRtrMixedEventTypesEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.VELOCITY_RTR_MIXED_EVENT_TYPES],
);

export const selectVelocityRtrDeviceIntelligenceFieldsEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) =>
    featureFlags[FeatureFlag.VELOCITY_RTR_DEVICE_INTELLIGENCE_FIELDS],
);

export const selectRtrTrmEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.RTR_TRM_ENABLED],
);

export const selectDmbFormRefactorEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.DMB_FORM_REFACTOR],
);

export const selectGlobalVariablesEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.GLOBAL_VARIABLES],
);

export const selectThresholdExplorerEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.THRESHOLD_EXPLORER],
);

export const selectThresholdExplorerDashboardsEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.THRESHOLD_EXPLORER_DASHBOARDS],
);

export const selectOnlineResearchBYOTEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.ONLINE_RESEARCH_BYOT],
);

export const selectGroundingOnlineResearchEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) =>
    Boolean(featureFlags[FeatureFlag.G_GROUNDING_ONLINE_RESEARCH]),
);

export const selectRuleRecommendationsEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.RULE_RECOMMENDATIONS],
);

export const selectRuleRecsCreateRuleEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.RULE_RECS_CREATE_RULE],
);

export const selectConsortiumDmbEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.CONSORTIUM_DMB],
);

export const selectConsortiumAlertsSummaryEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.CONSORTIUM_ALERTS_SUMMARY],
);

export const selectConsortiumAlertsV2Enabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.CONSORTIUM_ALERTS_V2],
);

export const selectRoundRobinAlertAssignmentEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.ROUND_ROBIN_ALERT_ASSIGNMENT],
);

export const selectEnableCgdoCommentsEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.ENABLE_CGDO_COMMENTS] ?? true,
);

export const selectWorkflowVisualizerEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.WORKFLOW_VISUALIZER],
);

export const selectWatchlistCombinedJourneysEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.WATCHLIST_COMBINED_JOURNEYS],
);

export const selectWatchlistRuleProjectionEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.WATCHLIST_RULE_PROJECTION],
);

export const selectTeamBasedWorkflowPermissionsEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.TEAM_BASED_WORKFLOW_PERMISSIONS],
);

export const selectWorkflowManagementRedesignEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.WORKFLOW_MANAGEMENT_REDESIGN],
);

export const selectFintracErrorCardsEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.FINTRAC_ERROR_CARDS],
);

export const selectRfiOnboardingEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.RFI_ONBOARDING],
);

export const selectRfiLivenessCheckEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => Boolean(featureFlags[FeatureFlag.LIVENESS_CHECK]),
);

export const selectAiTextToRuleEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.AI_TEXT_TO_RULE],
);

export const selectGreendotPivotTablesEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.GREENDOT_PIVOT_TABLES],
);

export const selectAiFuzzyMatchingEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.DMB_AI_FUZZY_SCORE],
);

export const selectMultiCaseFilingEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.MULTI_CASE_FILING],
);

export const selectMultiFilingInstitutionFintracStrEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) =>
    featureFlags[FeatureFlag.MULTI_FILING_INSTITUTION_FINTRAC_STR],
);

export const selectMultiFilingInstitutionSarEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.MULTI_FILING_INSTITUTION_SAR],
);

export const selectQueueDeadlineStartDateEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.QUEUE_DEADLINE_START_DATE],
);

export const selectBYOTForFilingsEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.BYOT_FOR_FILINGS],
);

export const selectRtrEntityAllowlistingEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.RTR_ENTITY_ALLOWLISTING],
);

export const selectFfipEnrichmentMatchingEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.FFIP_ENRICHMENT_MATCHING],
);

export const selectReportingDashboardV2Enabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.REPORTING_DASHBOARD_V2],
);

export const selectCrrMultipleLiveModelsEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.CRR_MULTIPLE_LIVE_MODELS],
);

export const selectCrrWaterfallSegmentationEnabled = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags[FeatureFlag.CRR_WATERFALL_SEGMENTATION],
);
